import React from 'react';

import { ScrollView, addWithConfig } from '@rbilabs/universal-components';
import {
  ErrorState,
  LoggedOutEmptyState,
  NoRecentOrdersEmptyState,
} from 'features/menu/pages/recent-items-page/recent-order-card-list/empty-state';
import RecentOrderCardGroup from 'features/menu/pages/recent-items-page/recent-order-card-list/recent-order-card-group';
import { useStoreContext } from 'features/store';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView as RNScrollView } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import ActionButton from 'components/action-button';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import { CenteredRowContainer } from 'components/ui/centered-row-container';
import useAddToCartRecentItem from 'hooks/use-add-to-cart-recent-item';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { useReorderableOrders } from 'hooks/use-reorderable-orders';
import { UserOrdersDefaultLimit } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';

const LOAD_MORE_SCROLL_OFFSET = 200;
const LOAD_MORE_FADE_IN_DURATION = 1500;

const LoadMoreButton = addWithConfig(ActionButton).withConfig({
  style: {
    marginLeft: '30%',
    width: '40%',
    marginBottom: '10%',
  },
});

const RecentOrderCardList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const scrollViewRef = React.useRef<RNScrollView>(null);
  const scrollOffsetYRef = React.useRef(0);

  const orderCountBeforeLoadMoreRef = React.useRef<number | null>(null);

  const nativeTabBarContentInset = useNativeTabBarContentInset();

  const { isAuthenticated } = useAuthContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const {
    serverOrder,
    recent: { pendingRecentItem, setPendingRecentItem },
  } = useOrderContext();
  const { addToCartIfAvailable } = useAddToCartRecentItem({ cartEntry: pendingRecentItem });

  const {
    reorderableOrders,
    count,
    isLoading,
    error,
    fetchingMore,
    handleLoadMore,
    refetch,
    resumeAfter,
  } = useReorderableOrders(
    {
      limit: UserOrdersDefaultLimit,
    },
    'no-cache'
  );

  const handleScroll = React.useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffsetYRef.current = event.nativeEvent.contentOffset.y;
  }, []);

  const handleLoadMorePress = React.useCallback(() => {
    orderCountBeforeLoadMoreRef.current = reorderableOrders.length;
    handleLoadMore();
  }, [handleLoadMore, reorderableOrders.length]);

  // Once "Load More" finishes, scroll down only if new visible orders were added.
  React.useEffect(() => {
    const orderCountBeforeLoadMore = orderCountBeforeLoadMoreRef.current;

    if (fetchingMore || orderCountBeforeLoadMore === null) {
      return;
    }

    orderCountBeforeLoadMoreRef.current = null;

    if (reorderableOrders.length > orderCountBeforeLoadMore) {
      scrollViewRef.current?.scrollTo({
        y: scrollOffsetYRef.current + LOAD_MORE_SCROLL_OFFSET,
        animated: true,
      });
    }
  }, [fetchingMore, reorderableOrders.length]);

  React.useEffect(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem && prices) {
      setPendingRecentItem(null);
      addToCartIfAvailable(pendingRecentItem);
    }
  }, [
    addToCartIfAvailable,
    isStoreOpenAndAvailable,
    pendingRecentItem,
    prices,
    setPendingRecentItem,
  ]);

  // TODO: this previously had an error - It was calling the effect with `undefined`. Check if the refetch is still valid
  // rbiOrderId = serverOrder?._id;
  const rbiOrderId = serverOrder?.rbiOrderId;
  useEffectOnUpdates(() => {
    refetch();
  }, [refetch, rbiOrderId]);

  if (isLoading && !fetchingMore) {
    return (
      <CenteredRowContainer>
        <ProductCardListSkeleton />
      </CenteredRowContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <LoggedOutEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (error) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <ErrorState onRefresh={() => refetch()} />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (!reorderableOrders.length && !resumeAfter) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <NoRecentOrdersEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      showsVerticalScrollIndicator={false}
      accessibilityLabel="recent-order-card-list-container"
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}
      onScroll={handleScroll}
      scrollEventThrottle={16}
    >
      <CenteredRowContainer>
        {reorderableOrders.map((order, index) => {
          const orderCountBeforeLoadMore = orderCountBeforeLoadMoreRef.current;
          const isNewlyLoadedOrder =
            orderCountBeforeLoadMore !== null && index >= orderCountBeforeLoadMore;

          return (
            <Animated.View
              key={order?.rbiOrderId}
              entering={
                isNewlyLoadedOrder ? FadeIn.duration(LOAD_MORE_FADE_IN_DURATION) : undefined
              }
            >
              <RecentOrderCardGroup order={order} />
            </Animated.View>
          );
        })}
        {count > 0 && resumeAfter && (
          <LoadMoreButton
            testID="load-more-button"
            onPress={handleLoadMorePress}
            isLoading={fetchingMore}
          >
            {t('loadMore')}
          </LoadMoreButton>
        )}
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default RecentOrderCardList;
